/**
 * UTF-8 safe base64 helpers for values that travel through the URL.
 *
 * `window.btoa` only accepts Latin1 (U+0000–U+00FF) and throws `InvalidCharacterError` on anything
 * else — curly quotes (’ “ ”), dashes (– —), or any non-Western character. Title names and licensor
 * labels regularly contain those, so never call `btoa`/`atob` directly on user or catalog data.
 *
 * `encodeBase64Utf8` encodes the string's UTF-8 bytes, so its output matches `btoa(input)` for
 * pure-ASCII input (U+0000–U+007F) only. Back-compat is therefore partial: ASCII URLs already in
 * the wild keep decoding, but ones carrying Latin1 characters (é à ñ ü) do not — `btoa` encoded
 * those as single bytes, which are not valid UTF-8. `TextDecoder` is non-fatal by default, so
 * `decodeBase64Utf8` replaces each such byte with U+FFFD instead of throwing: a legacy
 * `{"titleSearch":"Cléo de 5 à 7"}` link decodes to `{"titleSearch":"Cl�o de 5 � 7"}`,
 * which still parses as JSON, so the corruption surfaces as a filter that matches nothing rather
 * than as an error. See the spec for the worked example.
 */
export function encodeBase64Utf8(value: string): string {
	const bytes = new TextEncoder().encode(value);
	let binary = '';
	for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
	return btoa(binary);
}

/**
 * Inverse of {@link encodeBase64Utf8}. Throws on malformed base64 (same as `atob`), so callers that
 * read untrusted URL input should keep their existing try/catch. Bytes that are valid base64 but
 * invalid UTF-8 do NOT throw — they decode to U+FFFD, as described above.
 */
export function decodeBase64Utf8(value: string): string {
	const binary = atob(value);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
	return new TextDecoder().decode(bytes);
}

/** `encodeBase64Utf8(JSON.stringify(value))` — the shape every URL-persisted object/array uses. */
export function encodeUrlParam(value: unknown): string {
	return encodeBase64Utf8(JSON.stringify(value));
}

/** `JSON.parse(decodeBase64Utf8(value))`. Throws like `JSON.parse`/`atob` do. */
export function decodeUrlParam<T = unknown>(value: string): T {
	return JSON.parse(decodeBase64Utf8(value)) as T;
}
